import { useCallback, useEffect, useRef } from 'react';
import type Handsontable from 'handsontable';
import GridSuggestOverlay, {
  type GridSuggestOverlayHandle,
  type GridSuggestState,
} from '../components/GridSuggestOverlay';
import {
  computeSuggestDropdownPosition,
  filterSuggestOptions,
  findCanonicalSuggestValue,
  isStrictSuggestValue,
} from '../utils/gridSuggestDropdown';

export type SuggestSource = { title: string; options: string[] };

type HotRef = { current: { hotInstance?: Handsontable } | null };

interface UseHandsontableSuggestOptions {
  hotRef: HotRef;
  getSuggestSource: (row: number, col: number) => SuggestSource | null;
}

/**
 * Handsontable 셀용 커스텀 자동완성 (컨테이너 배차일보 / 조달실 공통)
 * - 타이핑 시 필터(대소문자 무시)
 * - Enter/클릭으로만 목록 값 확정
 * - 목록 외 값은 확정 시 되돌림, 일치 시 정식 표기로 정규화
 */
export function useHandsontableSuggest({
  hotRef,
  getSuggestSource,
}: UseHandsontableSuggestOptions) {
  const suggestEditorCleanupRef = useRef<(() => void) | null>(null);
  const suggestOriginalRef = useRef<{ row: number; col: number; value: string } | null>(null);
  const suggestEditingCellRef = useRef<{ row: number; col: number } | null>(null);
  const suggestSelectLockRef = useRef(false);
  const cellSuggestRef = useRef<GridSuggestState | null>(null);
  const suggestOverlayRef = useRef<GridSuggestOverlayHandle>(null);
  const getSuggestSourceRef = useRef(getSuggestSource);
  getSuggestSourceRef.current = getSuggestSource;

  const closeCellSuggest = useCallback(() => {
    suggestEditorCleanupRef.current?.();
    suggestEditorCleanupRef.current = null;
    suggestOverlayRef.current?.hide();
    cellSuggestRef.current = null;
    suggestEditingCellRef.current = null;
  }, []);

  const updateCellSuggest = useCallback((next: GridSuggestState | null) => {
    if (suggestSelectLockRef.current && next) return;
    cellSuggestRef.current = next;
    if (next) {
      suggestOverlayRef.current?.update(next);
    } else {
      suggestOverlayRef.current?.hide();
    }
  }, []);

  const isHandsontableEditing = useCallback((hot: Handsontable) => {
    const input = hot.rootElement?.querySelector('textarea.handsontableInput');
    return Boolean(input && document.activeElement === input);
  }, []);

  const validateSuggestCell = useCallback((row: number, col: number) => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const source = getSuggestSourceRef.current(row, col);
    if (!source) {
      suggestOriginalRef.current = null;
      suggestEditingCellRef.current = null;
      return;
    }
    const raw = String(hot.getDataAtCell(row, col) ?? '');
    const val = raw.trim();
    if (!val) {
      if (raw !== '') {
        hot.setDataAtCell(row, col, '', 'cd-strict-reject');
      }
      suggestOriginalRef.current = null;
      suggestEditingCellRef.current = null;
      return;
    }
    const canonical = findCanonicalSuggestValue(val, source.options);
    if (!canonical) {
      const original = suggestOriginalRef.current;
      const revert = original && original.row === row && original.col === col
        ? original.value
        : '';
      hot.setDataAtCell(row, col, revert, 'cd-strict-reject');
    } else if (canonical !== raw) {
      hot.setDataAtCell(row, col, canonical, 'cd-strict-normalize');
    }
    suggestOriginalRef.current = null;
    suggestEditingCellRef.current = null;
  }, [hotRef]);

  const scheduleSuggestValidation = useCallback((row: number, col: number) => {
    const run = () => {
      const hot = hotRef.current?.hotInstance;
      if (!hot) return;
      if (isHandsontableEditing(hot)) {
        window.setTimeout(run, 30);
        return;
      }
      validateSuggestCell(row, col);
      closeCellSuggest();
    };
    window.setTimeout(run, 0);
  }, [closeCellSuggest, hotRef, isHandsontableEditing, validateSuggestCell]);

  const handleAfterBeginEditing = useCallback((row: number, col: number) => {
    suggestEditorCleanupRef.current?.();
    suggestEditorCleanupRef.current = null;
    updateCellSuggest(null);

    const source = getSuggestSourceRef.current(row, col);
    if (!source) return;

    const hot = hotRef.current?.hotInstance;
    const td = hot?.getCell(row, col);
    const editor = hot?.getActiveEditor() as { TEXTAREA?: HTMLTextAreaElement } | undefined;
    const textarea = editor?.TEXTAREA;
    if (!td || !textarea) return;

    suggestSelectLockRef.current = false;
    suggestOriginalRef.current = {
      row,
      col,
      value: String(hot?.getDataAtCell(row, col) ?? ''),
    };
    suggestEditingCellRef.current = { row, col };

    const refreshSuggest = () => {
      if (suggestSelectLockRef.current) return;
      const query = textarea.value;
      const { items, total } = filterSuggestOptions(query, source.options, 12);
      if (items.length === 0) {
        updateCellSuggest(null);
        return;
      }
      const rect = td.getBoundingClientRect();
      const position = computeSuggestDropdownPosition(rect, items.length);
      const prev = cellSuggestRef.current;
      updateCellSuggest({
        title: source.title,
        items,
        totalCount: total,
        row,
        col,
        position,
        activeIndex: prev && prev.row === row && prev.col === col
          ? Math.min(prev.activeIndex, items.length - 1)
          : 0,
        query,
      });
    };

    refreshSuggest();
    textarea.addEventListener('input', refreshSuggest);
    suggestEditorCleanupRef.current = () => textarea.removeEventListener('input', refreshSuggest);
  }, [hotRef, updateCellSuggest]);

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!cellSuggestRef.current && !suggestEditingCellRef.current) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('.cd-grid-suggest')) return;
      if (target?.closest('.handsontableInput')) return;
      const pending = suggestEditingCellRef.current;
      closeCellSuggest();
      if (pending) {
        scheduleSuggestValidation(pending.row, pending.col);
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [closeCellSuggest, scheduleSuggestValidation]);

  const handleSelectSuggest = useCallback((value: string) => {
    const suggest = cellSuggestRef.current;
    if (!suggest) return;
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    const { row, col } = suggest;
    suggestSelectLockRef.current = true;
    closeCellSuggest();

    const editor = hot.getActiveEditor() as {
      isOpened?: () => boolean;
      TEXTAREA?: HTMLTextAreaElement;
      setValue?: (next: string) => void;
      finishEditing?: (restoreOriginalValue?: boolean, ctrlDown?: boolean) => void;
    } | undefined;

    if (editor?.isOpened?.()) {
      if (typeof editor.setValue === 'function') {
        editor.setValue(value);
      } else if (editor.TEXTAREA) {
        editor.TEXTAREA.value = value;
      }
      editor.finishEditing?.(false, false);
    } else {
      hot.setDataAtCell(row, col, value, 'edit');
    }

    window.setTimeout(() => {
      suggestOverlayRef.current?.hide();
      cellSuggestRef.current = null;
      suggestSelectLockRef.current = false;
    }, 0);
  }, [closeCellSuggest, hotRef]);

  const handleSuggestActiveIndexChange = useCallback((index: number) => {
    const suggest = cellSuggestRef.current;
    if (!suggest) return;
    cellSuggestRef.current = { ...suggest, activeIndex: index };
  }, []);

  const handleBeforeKeyDown = useCallback((event: KeyboardEvent) => {
    const suggest = cellSuggestRef.current;
    if (!suggest) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = Math.min(suggest.activeIndex + 1, suggest.items.length - 1);
      cellSuggestRef.current = { ...suggest, activeIndex: nextIndex };
      suggestOverlayRef.current?.setActiveIndex(nextIndex);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const nextIndex = Math.max(suggest.activeIndex - 1, 0);
      cellSuggestRef.current = { ...suggest, activeIndex: nextIndex };
      suggestOverlayRef.current?.setActiveIndex(nextIndex);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const selected = suggest.items[suggest.activeIndex];
      if (selected) {
        handleSelectSuggest(selected);
      } else {
        suggestSelectLockRef.current = true;
        closeCellSuggest();
        const hot = hotRef.current?.hotInstance;
        hot?.getActiveEditor()?.finishEditing?.(true, false);
        suggestSelectLockRef.current = false;
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      suggestSelectLockRef.current = true;
      closeCellSuggest();
      const hot = hotRef.current?.hotInstance;
      hot?.getActiveEditor()?.finishEditing?.(true, false);
      suggestSelectLockRef.current = false;
    }
  }, [closeCellSuggest, handleSelectSuggest, hotRef]);

  /** afterChange에서 호출 — 목록 외 값 거부/정규화. true면 저장해도 됨 */
  const enforceSuggestChange = useCallback((
    changes: Array<[number, number | string, unknown, unknown]>,
    source: string,
  ): Array<[number, number]> => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return [];
    if (source === 'cd-strict-reject') return [];

    const allowed: Array<[number, number]> = [];

    if (source === 'edit' || source === 'CopyPaste.paste' || source === 'Autofill.fill') {
      for (const [row, col, oldValue, newVal] of changes) {
        if (typeof row !== 'number' || typeof col !== 'number') continue;
        const suggestSource = getSuggestSourceRef.current(row, col);
        if (!suggestSource) {
          allowed.push([row, col]);
          continue;
        }

        if (source === 'edit') {
          suggestSelectLockRef.current = true;
          closeCellSuggest();
        }

        const raw = String(newVal ?? '');
        const val = raw.trim();
        if (!val) {
          if (raw !== '') {
            hot.setDataAtCell(row, col, '', 'cd-strict-reject');
          }
          suggestOriginalRef.current = null;
          allowed.push([row, col]);
          continue;
        }
        const canonical = findCanonicalSuggestValue(val, suggestSource.options);
        if (!canonical) {
          const revert = typeof oldValue === 'string' ? oldValue : (
            suggestOriginalRef.current
            && suggestOriginalRef.current.row === row
            && suggestOriginalRef.current.col === col
              ? suggestOriginalRef.current.value
              : ''
          );
          hot.setDataAtCell(row, col, revert, 'cd-strict-reject');
          suggestOriginalRef.current = null;
          continue;
        }
        if (canonical !== raw) {
          hot.setDataAtCell(row, col, canonical, 'cd-strict-normalize');
        }
        suggestOriginalRef.current = null;
        allowed.push([row, col]);
      }
      if (source === 'edit') {
        window.setTimeout(() => {
          suggestSelectLockRef.current = false;
        }, 0);
      }
      return allowed;
    }

    if (source === 'cd-strict-normalize') {
      for (const [row, col] of changes) {
        if (typeof row === 'number' && typeof col === 'number') {
          allowed.push([row, col]);
        }
      }
    }

    return allowed;
  }, [closeCellSuggest, hotRef]);

  const isSuggestChangeRejected = useCallback((
    row: number,
    col: number,
    newVal: unknown,
  ): boolean => {
    const suggestSource = getSuggestSourceRef.current(row, col);
    if (!suggestSource) return false;
    const val = String(newVal ?? '').trim();
    if (!val) return false;
    return !isStrictSuggestValue(val, suggestSource.options);
  }, []);

  const SuggestOverlay = (
    <GridSuggestOverlay
      ref={suggestOverlayRef}
      onSelect={handleSelectSuggest}
      onClose={closeCellSuggest}
      onActiveIndexChange={handleSuggestActiveIndexChange}
    />
  );

  return {
    SuggestOverlay,
    handleAfterBeginEditing,
    handleBeforeKeyDown,
    enforceSuggestChange,
    isSuggestChangeRejected,
    closeCellSuggest,
    scheduleSuggestValidation,
  };
}
